/**
 * Number Base Converter
 *
 * Provides conversion between binary, octal, decimal, and hexadecimal number systems.
 * Includes validation, formatting, and error handling capabilities.
 */

export interface NumberInput {
  value: string;
  base: 'binary' | 'octal' | 'decimal' | 'hexadecimal';
}

export interface NumberOutput {
  rawValue: string;
  inputBase: 'binary' | 'octal' | 'decimal' | 'hexadecimal';
  isValid: boolean;
  error?: string;
  decimalValue: number | null;
  binaryValue: string | null;
  octalValue: string | null;
  hexadecimalValue: string | null;
}

export interface BaseOption {
  value: 'binary' | 'octal' | 'decimal' | 'hexadecimal';
  label: string;
  description: string;
}

export class NumberBaseConverter {
  private readonly BASE_PATTERNS = {
    binary: /^[01]+$/,
    octal: /^[0-7]+$/,
    decimal: /^[0-9-]+$/,
    hexadecimal: /^[0-9A-Fa-f]+$/
  };

  private readonly BASE_RADIX = {
    binary: 2,
    octal: 8,
    decimal: 10,
    hexadecimal: 16
  };

  /**
   * Convert a number from one base to all supported bases
   */
  convert(input: string, inputBase: 'binary' | 'octal' | 'decimal' | 'hexadecimal'): NumberOutput {
    // Trim whitespace from input
    const trimmedInput = input.trim();

    // Handle empty input
    if (!trimmedInput) {
      return {
        rawValue: '',
        inputBase,
        isValid: false,
        error: 'Input cannot be empty',
        decimalValue: null,
        binaryValue: null,
        octalValue: null,
        hexadecimalValue: null
      };
    }

    // Validate input format
    if (!this.validateInput(trimmedInput, inputBase)) {
      return {
        rawValue: trimmedInput,
        inputBase,
        isValid: false,
        error: `Invalid ${inputBase} number format`,
        decimalValue: null,
        binaryValue: null,
        octalValue: null,
        hexadecimalValue: null
      };
    }

    try {
      // For binary input, preserve the original if it's too large for safe integer conversion
      if (inputBase === 'binary') {
        // Check if binary string is too large for safe integer conversion
        if (trimmedInput.length > 53) {
          // For very large binary strings, return the original binary value
          // and null for other conversions (can't safely convert)
          return {
            rawValue: trimmedInput,
            inputBase,
            isValid: true,
            decimalValue: null,
            binaryValue: trimmedInput,
            octalValue: null,
            hexadecimalValue: null
          };
        }
      }

      // Convert to decimal (base 10)
      const decimalValue = parseInt(trimmedInput, this.BASE_RADIX[inputBase]);

      // Handle invalid conversion (shouldn't happen if validation passes)
      if (isNaN(decimalValue)) {
        return {
          rawValue: trimmedInput,
          inputBase,
          isValid: false,
          error: 'Failed to convert number',
          decimalValue: null,
          binaryValue: null,
          octalValue: null,
          hexadecimalValue: null
        };
      }

      // Convert from decimal to all other bases
      const binaryValue = decimalValue < 0 ? null : decimalValue.toString(2);
      const octalValue = decimalValue < 0 ? null : decimalValue.toString(8);
      const hexadecimalValue = decimalValue < 0 ? null : decimalValue.toString(16).toUpperCase();

      return {
        rawValue: trimmedInput,
        inputBase,
        isValid: true,
        decimalValue,
        binaryValue,
        octalValue,
        hexadecimalValue
      };
    } catch (error) {
      return {
        rawValue: trimmedInput,
        inputBase,
        isValid: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        decimalValue: null,
        binaryValue: null,
        octalValue: null,
        hexadecimalValue: null
      };
    }
  }

  /**
   * Validate if input string is valid for the specified base
   */
  validateInput(input: string, base: 'binary' | 'octal' | 'decimal' | 'hexadecimal'): boolean {
    if (!input || input.trim() === '') {
      return false;
    }

    // Check if base is valid
    if (!base || !(base in this.BASE_PATTERNS)) {
      return false;
    }

    const trimmedInput = input.trim();
    const pattern = this.BASE_PATTERNS[base];

    return pattern.test(trimmedInput);
  }

  /**
   * Get supported number bases with their descriptions
   */
  getSupportedBases(): BaseOption[] {
    return [
      {
        value: 'binary',
        label: 'Binary',
        description: 'Base 2 (0-1)'
      },
      {
        value: 'octal',
        label: 'Octal',
        description: 'Base 8 (0-7)'
      },
      {
        value: 'decimal',
        label: 'Decimal',
        description: 'Base 10 (0-9)'
      },
      {
        value: 'hexadecimal',
        label: 'Hexadecimal',
        description: 'Base 16 (0-9, A-F)'
      }
    ];
  }

  /**
   * Format a number with leading zeros for the specified base
   */
  formatNumber(value: number, base: 'binary' | 'octal' | 'decimal' | 'hexadecimal', padding: number): string {
    if (value < 0) {
      return value.toString();
    }

    let formatted: string;

    switch (base) {
      case 'binary':
        formatted = value.toString(2);
        break;
      case 'octal':
        formatted = value.toString(8);
        break;
      case 'decimal':
        formatted = value.toString(10);
        break;
      case 'hexadecimal':
        formatted = value.toString(16).toUpperCase();
        break;
      default:
        formatted = value.toString();
    }

    // Apply padding if requested
    if (padding > 0 && base !== 'decimal') {
      formatted = formatted.padStart(padding, '0');
    }

    return formatted;
  }
}